import { Component } from '@theme/component';
import { morph } from '@theme/morph';

class SodQuickAdd extends Component {
  static #cache = new Map();

  #abortController = null;

  get #dialog() {
    const el = document.getElementById('quick-add-dialog');
    return el && typeof el.showDialog === 'function' ? el : null;
  }

  open = async (event) => {
    event?.preventDefault();

    const url = this.dataset.productUrl;
    const dialog = this.#dialog;
    const content = document.getElementById('quick-add-modal-content');
    if (!url || !dialog || !content) {
      if (url) window.location.href = url;
      return;
    }

    this.toggleAttribute('loading', true);
    try {
      let grid = SodQuickAdd.#cache.get(url);

      if (!grid) {
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        const response = await fetch(url, { signal: this.#abortController.signal });
        if (!response.ok) throw new Error(`quick add: HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const found = doc.querySelector('[data-product-grid-content]');
        if (!found) throw new Error('quick add: no product grid in the response');
        grid = found;
        SodQuickAdd.#cache.set(url, grid);
      }

      const fresh =  (grid.cloneNode(true));
      fresh.setAttribute('data-sod-quick-add-grid', '');
      content.setAttribute('view-event-payload', fresh.getAttribute('view-event-payload') || '');
      morph(content, fresh);
      this.#liftDetails(content);
      this.#appendDetailsLink(content, url);
      // In-page links like the rating's "#product-reviews" belong to the product page, not the page behind the dialog.
      for (const link of content.querySelectorAll('a[href^="#"]:not([href="#"])')) {
        link.href = url + link.getAttribute('href');
      }
      this.#instantArrows(content);

      dialog.showDialog();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.warn('[sod-quick-add]', error);
      window.location.href = url;
    } finally {
      this.#abortController = null;
      this.toggleAttribute('loading', false);
    }
  };

  #liftDetails(content) {
    const details = content.querySelector('.product-details');
    if (!details) return;

    const inner = details.querySelector('.group-block-content') ?? details;
    const column = document.createElement('div');
    column.className = 'sod-qa__details';
    column.append(...inner.childNodes);
    details.replaceWith(column);

    if (window.matchMedia('(max-width: 749px)').matches) {
      const media = content.querySelector('.product-information__media');
      const heading =
        column.querySelector('[class*="__description"]') ?? column.querySelector('.text-block:has(h1)');
      if (media && heading) heading.after(media);
    }
  }

  #instantArrows(content) {
    const slideshow = content.querySelector('slideshow-component');
    if (!slideshow) return;

    for (const button of content.querySelectorAll('slideshow-arrows .slideshow-control')) {
      if (button.hasAttribute('data-sod-instant')) continue;
      button.setAttribute('data-sod-instant', '');
      button.removeAttribute('on:click');
      button.addEventListener('click', (event) => {
        const method = button.classList.contains('slideshow-control--previous') ? 'previous' : 'next';
        if (typeof slideshow[method] !== 'function') return;
        event.preventDefault();
        slideshow[method](undefined, { animate: false });
      });
    }
  }

  #appendDetailsLink(content, url) {
    const label = this.dataset.linkLabel?.trim();
    if (!label) return;

    const column = content.querySelector('.sod-qa__details') ?? content;
    let link = column.querySelector(':scope > .sod-qa__link');
    if (!link) {
      link = document.createElement('a');
      link.className = 'sod-qa__link';
      column.append(link);
    }
    link.href = url;
    link.textContent = label;
  }
}

if (!customElements.get('sod-quick-add')) customElements.define('sod-quick-add', SodQuickAdd);
